import { useState } from "react";
import { PageLayout } from "@/components/PageLayout";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { CopyResultButton } from "@/components/CopyResultButton";

export default function LifeTableCalculator() {
  const [age, setAge] = useState("");
  const [currentPopulation, setCurrentPopulation] = useState("");
  const [deaths, setDeaths] = useState("");
  const [result, setResult] = useState<{
    qx: number;
    px: number;
    dx: number;
    lx1: number;
    lifeExpectancy: number;
  } | null>(null);

  const calculate = () => {
    const x = parseFloat(age);
    const lx = parseFloat(currentPopulation);
    const d = parseFloat(deaths);

    if (isNaN(x) || isNaN(lx) || isNaN(d) || x < 0 || lx <= 0 || d < 0 || d > lx) {
      toast.error("Please enter valid values (deaths cannot exceed population)");
      return;
    }

    // qx = probability of death (dx / lx)
    const qx = d / lx;
    
    // px = probability of survival (1 - qx)
    const px = 1 - qx;
    
    // dx = number of deaths
    const dx = d;
    
    // lx+1 = survivors to next age (lx - dx)
    const lx1 = lx - dx;
    
    // Simplified life expectancy (assuming constant mortality)
    const lifeExpectancy = 1 / qx;

    setResult({ qx, px, dx, lx1, lifeExpectancy });
    toast.success("Life table values calculated!");
  };

  const reset = () => {
    setAge("");
    setCurrentPopulation("");
    setDeaths("");
    setResult(null);
  };

  return (
    <PageLayout>
      <Card>
        <CardHeader>
          <CardTitle>Life Table Calculator</CardTitle>
          <CardDescription>
            Calculate mortality rates and life expectancy metrics
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="age">Age (x)</Label>
            <Input
              id="age"
              type="number"
              placeholder="65"
              value={age}
              onChange={(e) => setAge(e.target.value)}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="population">Population at Age x (lx)</Label>
            <Input
              id="population"
              type="number"
              placeholder="100000"
              value={currentPopulation}
              onChange={(e) => setCurrentPopulation(e.target.value)}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="deaths">Deaths Between Age x and x+1 (dx)</Label>
            <Input
              id="deaths"
              type="number"
              placeholder="2000"
              value={deaths}
              onChange={(e) => setDeaths(e.target.value)}
            />
          </div>
          <div className="flex gap-2">
            <Button onClick={calculate}>Calculate</Button>
            <Button onClick={reset} variant="outline">Reset</Button>
          </div>
          {result !== null && (
            <div className="mt-4 space-y-2">
              <div className="p-3 bg-muted rounded-lg flex items-center justify-between">
                <div>
                  <strong>Mortality Rate (qx):</strong> {(result.qx * 100).toFixed(4)}%
                  <p className="text-xs text-muted-foreground">Probability of death</p>
                </div>
                <CopyResultButton value={(result.qx * 100).toFixed(4)} />
              </div>
              <div className="p-3 bg-muted rounded-lg flex items-center justify-between">
                <div>
                  <strong>Survival Rate (px):</strong> {(result.px * 100).toFixed(4)}%
                  <p className="text-xs text-muted-foreground">Probability of survival</p>
                </div>
                <CopyResultButton value={(result.px * 100).toFixed(4)} />
              </div>
              <div className="p-3 bg-muted rounded-lg flex items-center justify-between">
                <div>
                  <strong>Deaths (dx):</strong> {result.dx.toFixed(0)}
                  <p className="text-xs text-muted-foreground">Number dying in interval</p>
                </div>
                <CopyResultButton value={result.dx.toFixed(0)} />
              </div>
              <div className="p-3 bg-muted rounded-lg flex items-center justify-between">
                <div>
                  <strong>Survivors (lx+1):</strong> {result.lx1.toFixed(0)}
                  <p className="text-xs text-muted-foreground">Living at next age</p>
                </div>
                <CopyResultButton value={result.lx1.toFixed(0)} />
              </div>
              <div className="p-3 bg-muted rounded-lg flex items-center justify-between">
                <div>
                  <strong>Life Expectancy:</strong> {result.lifeExpectancy.toFixed(2)} years
                  <p className="text-xs text-muted-foreground">Expected remaining years</p>
                </div>
                <CopyResultButton value={result.lifeExpectancy.toFixed(2)} />
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </PageLayout>
  );
}
